import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronRight, Home, Clock, MapPin, User, Phone, Pill,
  AlertTriangle, ClipboardList, FileText, Camera, Truck, Play, Square,
  CheckCircle, ShieldAlert, ListChecks,
} from 'lucide-react';
import { isOnline } from '@/lib/offline/syncEngine';
import { secureStore, TTL } from '@/lib/offline/secureStore';
import { MobileBottomNav } from '@/components/mobile/MobileNavigation';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';
import MobileCheckInDialog from '@/components/mobile/MobileCheckInDialog';
import MobileShiftChecklist from '@/components/mobile/MobileShiftChecklist';
import { WorkerShiftParticipantsPanel } from '@/components/shared/WorkerShiftParticipantsPanel';

function Section({ title, icon: Icon, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">{title}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function ActionTile({ icon: Icon, label, onClick, variant = 'default', disabled }) {
  const variants = {
    default: 'bg-card border-2 hover:border-primary/50',
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    danger: 'bg-red-500 text-white hover:bg-red-600',
    success: 'bg-green-500 text-white hover:bg-green-600',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all active:scale-95 disabled:opacity-40 ${variants[variant]}`}
      style={{ minHeight: 72 }}
      data-testid={`shift-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-6 w-6 mb-1" />
      <span className="text-xs font-medium text-center leading-tight">{label}</span>
    </button>
  );
}

export default function MobileShiftDetailPage() {
  const navigate = useNavigate();
  const { shiftId } = useParams();
  const [online] = useState(isOnline());
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [checkDialog, setCheckDialog] = useState(null); // 'in' | 'out' | null

  const cacheKey = `shift_detail:${shiftId}`;

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    setNotFound(false);
    try {
      const res = await api.get(`/mobile/shifts/${shiftId}`);
      const payload = res.data?.data || res.data;
      setData(payload);
      setFromCache(false);
      // Offline snapshot — cache support-worker-safe shift context (Day 13)
      try { await secureStore.setItem(cacheKey, payload, TTL.SHIFT_META); } catch (_) { /* noop */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) { setForbidden(true); }
      else if (status === 404) { setNotFound(true); }
      else {
        // Network/server error — fall back to the encrypted offline snapshot
        let snapshot = null;
        try { snapshot = await secureStore.getItem(cacheKey); } catch (_) { /* noop */ }
        if (snapshot) {
          setData(snapshot);
          setFromCache(true);
          toast.info('Showing saved offline copy of this shift');
        } else {
          toast.error('Could not load shift details. Check your connection and try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [shiftId, cacheKey]);

  useEffect(() => { load(); }, [load]);

  if (forbidden) {
    return (
      <MobileAccessDenied
        title="Shift not assigned"
        message="You are not assigned to this shift, so you cannot open it. Please contact your coordinator if you believe this is a mistake."
        onBack={() => navigate('/mobile')}
      />
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center" data-testid="shift-not-found">
        <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
          <Clock className="h-10 w-10 text-muted-foreground" />
        </div>
        <h1 className="text-xl font-semibold mb-2">Shift not found</h1>
        <p className="text-sm text-muted-foreground max-w-sm mb-8">This shift no longer exists or has been removed.</p>
        <Button onClick={() => navigate('/mobile')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Button>
      </div>
    );
  }

  const shift = data?.shift;
  const participant = data?.participant;
  const site = data?.site;
  const tasks = data?.tasks || [];
  const careAlerts = data?.careAlerts || [];
  const medications = data?.medications || [];
  const emergencyContacts = data?.emergencyContacts || [];
  const previousHandover = data?.previousHandover;
  const exceptionStatus = data?.exceptionStatus;
  const workerContext = data?.workerContext;

  const readOnly = !!shift?.isReadOnly;
  const pid = participant?.id || shift?.participant_id;

  const qp = (extra = {}) => {
    const params = new URLSearchParams({ shiftId, ...(pid ? { participantId: pid } : {}), ...extra });
    return params.toString();
  };

  // Day 9: evidence prompts / panel actions route into the EXISTING mobile forms
  const handlePromptAction = (p) => {
    const extra = p.participantId ? { participantId: p.participantId } : {};
    if (p.action === 'NOTE') navigate(`/mobile/notes/new?${qp(extra)}`);
    else if (p.action === 'TRANSPORT') navigate(`/mobile/transport/new?shift_id=${shiftId}`);
    else if (p.action === 'ATTACHMENT') navigate(`/mobile/attachments?entityType=SHIFT&entityId=${shiftId}`);
    else if (p.action === 'CHECK_OUT') setCheckDialog('out');
  };

  const handleCheckIn = async () => {
    if (readOnly || shift?.isCheckedIn) return;
    setCheckDialog('in');
  };

  const handleCheckOut = async () => {
    if (!shift?.isActive) return;
    setCheckDialog('out');
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-shift-detail">
      {/* Header + breadcrumb */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="-ml-2" onClick={() => navigate('/mobile')} data-testid="shift-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="shift-breadcrumb">
              <button onClick={() => navigate('/mobile')} className="flex items-center gap-1 hover:text-foreground">
                <Home className="h-3 w-3" /> Home
              </button>
              <ChevronRight className="h-3 w-3" />
              <span>Shift</span>
            </div>
            <h1 className="text-base font-bold truncate">{participant?.name || shift?.title || 'Shift details'}</h1>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-4 space-y-4" data-testid="shift-detail-skeleton">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
      ) : !shift ? (
        <div className="p-6 text-center text-muted-foreground">Unable to load shift.</div>
      ) : (
        <div className="p-4 space-y-4">
          {fromCache && (
            <div className="flex items-center gap-2 rounded-xl bg-blue-500/10 border border-blue-500/20 px-4 py-3 text-blue-700 text-sm" data-testid="shift-offline-banner">
              <ShieldAlert className="h-4 w-4" />
              Offline — showing a saved copy of this shift. Some details may be out of date.
            </div>
          )}
          {readOnly && (
            <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-amber-700 text-sm" data-testid="shift-readonly-banner">
              <ShieldAlert className="h-4 w-4" />
              {shift.isCancelled ? 'This shift was cancelled — it is read-only.' : 'This shift is completed — it is read-only.'}
            </div>
          )}

          {shift.missedCheckout && (
            <div className="flex items-center gap-2 rounded-xl bg-red-500/10 border border-red-500/20 px-4 py-3 text-red-700 text-sm" data-testid="shift-missed-checkout-banner">
              <AlertTriangle className="h-4 w-4" />
              Missed check-out — this shift's scheduled end has passed. Please check out now.
            </div>
          )}

          {shift.lateCheckIn && (
            <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-amber-700 text-sm" data-testid="shift-late-banner">
              <AlertTriangle className="h-4 w-4" />
              Late check-in flagged{shift.lateByMinutes ? ` (${shift.lateByMinutes} min late)` : ''}.
            </div>
          )}

          {exceptionStatus && (
            <div
              className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm border ${
                exceptionStatus.status === 'APPROVED' ? 'bg-green-500/10 border-green-500/20 text-green-700'
                : exceptionStatus.status === 'REJECTED' ? 'bg-red-500/10 border-red-500/20 text-red-700'
                : 'bg-amber-500/10 border-amber-500/20 text-amber-700'
              }`}
              data-testid="shift-exception-status"
            >
              <ShieldAlert className="h-4 w-4" />
              <div>
                <p className="font-medium">
                  Check-in exception · {exceptionStatus.status === 'PENDING' ? 'Pending review'
                    : exceptionStatus.status === 'APPROVED' ? 'Approved' : 'Rejected'}
                </p>
                <p className="text-xs opacity-90">
                  {(exceptionStatus.reasons || []).join(', ')}
                  {exceptionStatus.reviewNotes ? ` — "${exceptionStatus.reviewNotes}"` : ''}
                </p>
              </div>
            </div>
          )}

          {/* Shift time + participant */}
          <Section title="Shift" icon={Clock} testid="shift-time-section">
            <div className="space-y-1 text-sm">
              <p className="font-medium">{shift.date} · {shift.start_time}{shift.end_time ? ` – ${shift.end_time}` : ''}</p>
              {shift.shift_type && <p className="text-xs text-muted-foreground">{shift.shift_type}{shift.support_ratio ? ` · ${shift.support_ratio}` : ''}</p>}
              {shift.notes && <p className="text-xs text-muted-foreground pt-1" data-testid="shift-notes">{shift.notes}</p>}
              {participant && (
                <div className="flex items-center gap-2 pt-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <button
                    className="text-primary underline-offset-2 hover:underline text-left"
                    onClick={() => navigate(`/mobile/participants/${pid}`)}
                    data-testid="open-participant-profile"
                  >
                    {participant.name}{participant.preferredName ? ` (${participant.preferredName})` : ''}
                  </button>
                  {participant.phone && (
                    <a href={`tel:${participant.phone}`} className="ml-auto text-primary text-xs flex items-center gap-1" data-testid="participant-call">
                      <Phone className="h-3 w-3" /> Call
                    </a>
                  )}
                </div>
              )}
              {pid && (
                <button
                  className="flex items-center gap-2 pt-2 text-primary text-sm underline-offset-2 hover:underline"
                  onClick={() => navigate(`/mobile/participants/${pid}/care-plan`)}
                  data-testid="open-care-plan-brief"
                >
                  <ClipboardList className="h-4 w-4" /> View Care Plan Brief
                </button>
              )}
            </div>
          </Section>

          {/* Day 9: participants on this shift (multi-participant, participant-specific times/tasks/goals/notes/attendance, evidence prompts) */}
          <WorkerShiftParticipantsPanel
            shiftId={shiftId}
            shiftDate={shift.date}
            context={workerContext}
            onRefresh={load}
            onAddNote={(participantId) => navigate(`/mobile/notes/new?${qp({ participantId })}`)}
            onOpenProfile={(participantId) => navigate(`/mobile/participants/${participantId}`)}
            onPromptAction={handlePromptAction}
            compact
          />

          {/* Site / address */}
          {site && (
            <Section title="Site / Address" icon={MapPin} testid="shift-site-section">
              <div className="text-sm space-y-1">
                {site.name && <p className="font-medium">{site.name}</p>}
                {(site.address || site.suburb) && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent([site.address, site.suburb, site.postcode].filter(Boolean).join(' '))}`}
                    target="_blank" rel="noreferrer"
                    className="text-primary text-xs flex items-center gap-1"
                    data-testid="site-map-link"
                  >
                    <MapPin className="h-3 w-3" /> {[site.address, site.suburb, site.postcode].filter(Boolean).join(', ')}
                  </a>
                )}
              </div>
            </Section>
          )}

          {/* Care alerts */}
          <Section title="Care Alerts" icon={AlertTriangle} testid="shift-alerts-section">
            {careAlerts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No care alerts for this participant.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {careAlerts.map((a, i) => (
                  <Badge key={i} variant="outline" className="bg-red-500/5 text-red-600 border-red-500/20 text-xs" data-testid="care-alert-item">
                    {a.label}
                  </Badge>
                ))}
              </div>
            )}
          </Section>

          {/* Tasks */}
          <Section title="Assigned Tasks" icon={ListChecks} testid="shift-tasks-section">
            {tasks.length === 0 ? (
              <p className="text-xs text-muted-foreground">No tasks assigned for this shift.</p>
            ) : (
              <ul className="space-y-2">
                {tasks.map((t, i) => (
                  <li key={t.id || i} className="flex items-center gap-2 text-sm" data-testid="task-item">
                    <CheckCircle className={`h-4 w-4 ${t.completed ? 'text-green-500' : 'text-muted-foreground'}`} />
                    <span className={t.completed ? 'line-through text-muted-foreground' : ''}>{t.title || 'Task'}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Task Checklist (Day 18) */}
          <MobileShiftChecklist shiftId={shiftId} readOnly={readOnly} />

          {/* Medications */}
          <Section title="Medications Due" icon={Pill} testid="shift-medications-section">
            {medications.length === 0 ? (
              <p className="text-xs text-muted-foreground">No medications recorded for this participant.</p>
            ) : (
              <ul className="space-y-2">
                {medications.map((m) => (
                  <li key={m.id} className="text-sm flex items-center gap-2" data-testid="medication-item">
                    <Pill className="h-4 w-4 text-amber-500" />
                    <span className="font-medium">{m.name}</span>
                    {m.dosage && <span className="text-xs text-muted-foreground">{m.dosage}</span>}
                    {m.prn && <Badge variant="outline" className="text-[10px]">PRN</Badge>}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Emergency contacts */}
          <Section title="Emergency Contacts" icon={Phone} testid="shift-contacts-section">
            {emergencyContacts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No emergency contacts on file.</p>
            ) : (
              <ul className="space-y-2">
                {emergencyContacts.map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm" data-testid="emergency-contact-item">
                    <div>
                      <p className="font-medium">{c.name || c.label}</p>
                      <p className="text-xs text-muted-foreground">{c.label}{c.phone ? ` · ${c.phone}` : ''}</p>
                    </div>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="text-primary text-xs flex items-center gap-1">
                        <Phone className="h-3 w-3" /> Call
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Previous handover */}
          <Section title="Previous Handover" icon={ClipboardList} testid="shift-handover-section">
            {!previousHandover ? (
              <p className="text-xs text-muted-foreground">No previous handover for this participant.</p>
            ) : (
              <div className="text-sm space-y-1">
                {previousHandover.summary && <p>{previousHandover.summary}</p>}
                {previousHandover.concerns && <p className="text-xs text-amber-700">Concerns: {previousHandover.concerns}</p>}
                {previousHandover.tasksForNextShift && <p className="text-xs text-muted-foreground">For next shift: {previousHandover.tasksForNextShift}</p>}
                {previousHandover.createdAt && <p className="text-[10px] text-muted-foreground pt-1">{previousHandover.createdAt}</p>}
              </div>
            )}
          </Section>

          {/* Check in / out */}
          {!readOnly && (
            <div className="flex gap-2">
              {shift.isActive ? (
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleCheckOut} data-testid="shift-checkout-btn">
                  <Square className="h-4 w-4 mr-2" /> End Shift
                </Button>
              ) : (
                <Button className="flex-1" onClick={handleCheckIn} disabled={shift.isCheckedIn} data-testid="shift-checkin-btn">
                  <Play className="h-4 w-4 mr-2" /> {shift.isCheckedIn ? 'Checked In' : 'Check In'}
                </Button>
              )}
            </div>
          )}

          {/* Action buttons — all pass shift_id into the forms */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 px-1">ACTIONS</p>
            <div className="grid grid-cols-3 gap-2" data-testid="shift-actions-grid">
              <ActionTile icon={FileText} label="Note" disabled={readOnly} onClick={() => navigate(`/mobile/notes/new?${qp()}`)} />
              <ActionTile icon={AlertTriangle} label="Incident" variant="danger" onClick={() => navigate(`/mobile/incidents/new?${qp()}`)} />
              <ActionTile icon={ClipboardList} label="Handover" disabled={readOnly} onClick={() => navigate(`/mobile/handover/new?${qp()}`)} />
              <ActionTile icon={Pill} label="Medication" disabled={readOnly} onClick={() => navigate(`/mobile/medications/log?${qp()}`)} />
              <ActionTile icon={Truck} label="Transport" onClick={() => navigate(`/mobile/transport/new?shift_id=${shiftId}`)} />
              <ActionTile icon={Camera} label="Attachment" onClick={() => navigate(`/mobile/attachments?entityType=SHIFT&entityId=${shiftId}`)} />
            </div>
          </div>
        </div>
      )}

      <MobileBottomNav />

      {checkDialog && shift && (
        <MobileCheckInDialog
          open={!!checkDialog}
          onOpenChange={(v) => setCheckDialog(v ? checkDialog : null)}
          mode={checkDialog}
          shift={shift}
          onDone={load}
        />
      )}
    </div>
  );
}
